/**
 * Hitstop gate — pure render-side timer mirroring the sim's kick hitstop.
 *
 * The sim freezes for `TUNING.kick.hitstop` seconds (HITSTOP_STEPS ticks)
 * after a connecting kick. The render side mirrors that window with a wall-
 * clock timer armed by the kick event itself.
 *
 * WHY A TIMER AND NOT TIME-SNIFFING: the previous detection compared
 * `state.time` between consecutive RENDER frames and treated "time did not
 * advance" as hitstop. That is only valid when every render frame contains
 * exactly one sim step (a 60 Hz display). On any faster display (120 Hz
 * ProMotion, 144 Hz…) half or more of the rAF frames legitimately contain no
 * sim step, so the flag toggled on alternating frames during NORMAL play and
 * the hitstop FOV zoom (-3°) was applied every other frame — a continuous
 * whole-scene zoom pump at the sim-tick frequency ("everything vibrates").
 *
 * AT hitstop === 0 (the current tuning — the launch is instant) the gate is a
 * strict no-op: `trigger()` stores 0, and `update()` returns false on the very
 * first frame because `remaining <= 0`, so `setHitstop(true)` is never reached
 * and no FOV zoom is ever applied. No special-casing needed.
 *
 * Zero allocation; pure arithmetic — safe to unit-test headlessly.
 */
import { TUNING } from "../sim/tuning";

export class HitstopGate {
  /** Wall-clock seconds of hitstop zoom remaining. */
  private remaining = 0;

  /** Arm the gate — call from the kick-connect event handler (the same
   *  event that makes the sim set its own hitstopRemaining). */
  trigger(): void {
    this.remaining = TUNING.kick.hitstop;
  }

  reset(): void {
    this.remaining = 0;
  }

  /**
   * Advance by `dt` render seconds. Returns whether the hitstop zoom is
   * active this frame. Only ever active during the 'playing' phase.
   */
  update(dt: number, playing: boolean): boolean {
    if (!playing) {
      this.remaining = 0;
      return false;
    }
    if (this.remaining <= 0) return false;
    this.remaining = Math.max(0, this.remaining - dt);
    return true;
  }
}
